import { Node, NodePositionChange, XYPosition } from "reactflow";

// Constants
const SNAP_DISTANCE = 16; // Target distance for snapping to 16px
const FIXED_LINE_LENGTH = 32; // Desired fixed length for distance helper lines
const FIXED_LINE_HALF_LENGTH = FIXED_LINE_LENGTH / 2;
const SNAP_DISTANCE_PROXIMITY = 4; // Proximity range to trigger snapping (within 4px of target distance)
const ALIGNMENT_PROXIMITY = 8; // Max distance for general alignment snapping
const DISTRIBUTION_SNAP_PROXIMITY = 6; // Max distance for distribution snapping

export type HelperLineSegment = {
  x1: number; y1: number;    // Start point of the line segment (flow coordinates)
  x2: number; y2: number;    // End point of the line segment (flow coordinates)
  style?: 'align' | 'spacing' | 'fixed'; // Style hint for rendering
};

export type GetHelperLinesResult = {
  lines: HelperLineSegment[];          // Array of line segments to draw
  snapPosition: Partial<XYPosition>; // The position the node should snap to
};

// Helper to create full-length alignment lines
const createFullLengthLine = (
  pos: number,
  type: 'horizontal' | 'vertical',
  viewMin: number,
  viewMax: number
): HelperLineSegment => {
  if (type === 'vertical') {
    return { x1: pos, y1: viewMin, x2: pos, y2: viewMax, style: 'align' };
  } else {
    return { x1: viewMin, y1: pos, x2: viewMax, y2: pos, style: 'align' };
  }
};

/**
 * This utility function calculates helper line positions and snap positions for node alignment and distribution.
 * It checks all other nodes and calculates where the current node should snap to.
 *
 * @param change The node position change
 * @param nodes All nodes in the flow
 * @param parentId The parent ID of the node being dragged, if any
 * @param viewBounds The current viewport boundaries in flow coordinates {x, y, x2, y2}
 * @param enableDistributionSnapping Flag to enable/disable distribution snapping
 * @returns Helper line segments and the final snap position
 */
export function getHelperLines(
  change: NodePositionChange,
  nodes: Node[],
  parentId: string | null = null,
  viewBoundsInput: { x: number; y: number; x2: number; y2: number } | undefined, // Changed to allow undefined
  enableSnapping = true
): GetHelperLinesResult {
  // If snapping is disabled, return empty result immediately
  if (!enableSnapping) {
    return {
      lines: [],
      snapPosition: { x: undefined, y: undefined },
    };
  }
  
  // Enable distribution snapping by default when snapping is enabled
  const enableDistributionSnapping = enableSnapping;
  // Workaround: Provide default large viewBounds if not supplied, to make alignment lines potentially visible
  // IMPORTANT: The user's integration code should calculate and pass correct viewBounds.
  const viewBounds = viewBoundsInput ?? { x: -10000, y: -10000, x2: 10000, y2: 10000 };

  const result: GetHelperLinesResult = {
    lines: [],
    snapPosition: { x: undefined, y: undefined },
  };

  const nodeA = nodes.find((node) => node.id === change.id);

  if (!nodeA || typeof nodeA.width !== 'number' || typeof nodeA.height !== 'number') {
    return result;
  }

  let absPosNodeA: XYPosition | undefined;
  if (change.positionAbsolute) {
    absPosNodeA = change.positionAbsolute;
  } else if (change.position) {
    if (nodeA.parentId) { // nodeA is a child, change.position is relative
      const parentNode = nodes.find(n => n.id === nodeA.parentId);
      if (parentNode?.positionAbsolute) {
        absPosNodeA = {
          x: parentNode.positionAbsolute.x + change.position.x,
          y: parentNode.positionAbsolute.y + change.position.y,
        };
      }
    } else { // Root node, change.position is absolute
      absPosNodeA = change.position;
    }
  }

  if (!absPosNodeA) {
    return result; // Cannot determine absolute position of the dragged node
  }

  const nodeABounds = {
    left: absPosNodeA.x,
    right: absPosNodeA.x + nodeA.width,
    top: absPosNodeA.y,
    bottom: absPosNodeA.y + nodeA.height,
    hCenter: absPosNodeA.x + nodeA.width / 2,
    vCenter: absPosNodeA.y + nodeA.height / 2,
    width: nodeA.width,
    height: nodeA.height,
  };

  let minVerticalDistance = ALIGNMENT_PROXIMITY;
  let minHorizontalDistance = ALIGNMENT_PROXIMITY;

  const otherNodes = nodes.filter(
    (node): node is Node & { width: number; height: number; position: XYPosition } => // Type predicate for clarity
      node.id !== nodeA.id &&
      (node.parentId ?? null) === (parentId ?? null) &&
      typeof node.width === 'number' && // Ensure width is a number
      typeof node.height === 'number' && // Ensure height is a number
      !!node.position // Ensure position is truthy (it's non-optional in type, but good practice)
  );

  // --- Stage 1: Alignment and Fixed Distance Snapping ---
  otherNodes.forEach((nodeB) => {
    // nodeB.width, .height, .position are guaranteed by the otherNodes filter.
    let absPosNodeB: XYPosition | undefined;
    if (nodeB.positionAbsolute) {
      absPosNodeB = nodeB.positionAbsolute;
    } else {
      // nodeB.position is guaranteed. If nodeB is a child, its position is relative.
      if (nodeB.parentId) { // This implies parentId (of nodeA) is also nodeB.parentId
        const parentOfB = nodes.find(n => n.id === nodeB.parentId);
        if (parentOfB?.positionAbsolute) {
          absPosNodeB = {
            x: parentOfB.positionAbsolute.x + nodeB.position.x,
            y: parentOfB.positionAbsolute.y + nodeB.position.y,
          };
        }
      } else { // nodeB is a root node, its nodeB.position is absolute
        absPosNodeB = nodeB.position;
      }
    }

    if (!absPosNodeB) {
      return; // Skip this nodeB if its absolute position cannot be determined
    }

    const nodeBBounds = {
      left: absPosNodeB.x,
      right: absPosNodeB.x + nodeB.width,
      top: absPosNodeB.y,
      bottom: absPosNodeB.y + nodeB.height,
      hCenter: absPosNodeB.x + nodeB.width / 2,
      vCenter: absPosNodeB.y + nodeB.height / 2,
      width: nodeB.width,
      height: nodeB.height,
    };

    // Vertical Snapping (Edges and Centers)
    const bVerticalPoints = [nodeBBounds.left, nodeBBounds.right, nodeBBounds.hCenter];
    const aVerticalPoints = [nodeABounds.left, nodeABounds.right, nodeABounds.hCenter];
    const aVerticalOffsets = [0, nodeABounds.width, nodeABounds.width / 2];

    bVerticalPoints.forEach(bVertEdge => {
      aVerticalPoints.forEach((_aVertEdge, index) => {
        const d = Math.abs(nodeABounds.left + aVerticalOffsets[index] - bVertEdge);
        if (d < minVerticalDistance) {
          minVerticalDistance = d;
          result.snapPosition.x = bVertEdge - aVerticalOffsets[index];
          result.lines = result.lines.filter(l => l.x1 !== l.x2 || l.style !== 'align'); // Clear previous vertical alignment lines
          result.lines.push(createFullLengthLine(bVertEdge, 'vertical', viewBounds.y, viewBounds.y2));
        }
      });
    });
    
    // Horizontal Snapping (Edges and Centers)
    const bHorizontalPoints = [nodeBBounds.top, nodeBBounds.bottom, nodeBBounds.vCenter];
    const aHorizontalPoints = [nodeABounds.top, nodeABounds.bottom, nodeABounds.vCenter];
    const aHorizontalOffsets = [0, nodeABounds.height, nodeABounds.height / 2];

    bHorizontalPoints.forEach(bHorzEdge => {
      aHorizontalPoints.forEach((_aHorzEdge, index) => {
        const d = Math.abs(nodeABounds.top + aHorizontalOffsets[index] - bHorzEdge);
        if (d < minHorizontalDistance) {
          minHorizontalDistance = d;
          result.snapPosition.y = bHorzEdge - aHorizontalOffsets[index];
          result.lines = result.lines.filter(l => l.y1 !== l.y2 || l.style !== 'align'); // Clear previous horizontal alignment lines
          result.lines.push(createFullLengthLine(bHorzEdge, 'horizontal', viewBounds.x, viewBounds.x2));
        }
      });
    });

    // Fixed Distance Snapping (16px)
    // A_Right to B_Left
    let dist = nodeBBounds.left - nodeABounds.right;
    if (Math.abs(dist - SNAP_DISTANCE) < SNAP_DISTANCE_PROXIMITY && Math.abs(dist - SNAP_DISTANCE) < minVerticalDistance) {
        minVerticalDistance = Math.abs(dist - SNAP_DISTANCE);
        result.snapPosition.x = nodeBBounds.left - SNAP_DISTANCE - nodeABounds.width;
        const linePos = nodeBBounds.left - SNAP_DISTANCE / 2;
        result.lines = result.lines.filter(l => l.x1 !== l.x2 || l.style === 'align'); // Keep align, remove other fixed/spacing
        result.lines.push({
          x1: linePos, y1: nodeABounds.vCenter - FIXED_LINE_HALF_LENGTH,
          x2: linePos, y2: nodeABounds.vCenter + FIXED_LINE_HALF_LENGTH,
          style: 'fixed'
        });
    }
    // A_Left to B_Right
    dist = nodeABounds.left - nodeBBounds.right;
    if (Math.abs(dist - SNAP_DISTANCE) < SNAP_DISTANCE_PROXIMITY && Math.abs(dist - SNAP_DISTANCE) < minVerticalDistance) {
        minVerticalDistance = Math.abs(dist - SNAP_DISTANCE);
        result.snapPosition.x = nodeBBounds.right + SNAP_DISTANCE;
        const linePos = nodeBBounds.right + SNAP_DISTANCE / 2;
        result.lines = result.lines.filter(l => l.x1 !== l.x2 || l.style === 'align');
        result.lines.push({
          x1: linePos, y1: nodeABounds.vCenter - FIXED_LINE_HALF_LENGTH,
          x2: linePos, y2: nodeABounds.vCenter + FIXED_LINE_HALF_LENGTH,
          style: 'fixed'
        });
    }
    // A_Bottom to B_Top
    dist = nodeBBounds.top - nodeABounds.bottom;
    if (Math.abs(dist - SNAP_DISTANCE) < SNAP_DISTANCE_PROXIMITY && Math.abs(dist - SNAP_DISTANCE) < minHorizontalDistance) {
        minHorizontalDistance = Math.abs(dist - SNAP_DISTANCE);
        result.snapPosition.y = nodeBBounds.top - SNAP_DISTANCE - nodeABounds.height;
        const linePos = nodeBBounds.top - SNAP_DISTANCE / 2;
        result.lines = result.lines.filter(l => l.y1 !== l.y2 || l.style === 'align');
        result.lines.push({
          x1: nodeABounds.hCenter - FIXED_LINE_HALF_LENGTH, y1: linePos,
          x2: nodeABounds.hCenter + FIXED_LINE_HALF_LENGTH, y2: linePos,
          style: 'fixed'
        });
    }
    // A_Top to B_Bottom
    dist = nodeABounds.top - nodeBBounds.bottom;
    if (Math.abs(dist - SNAP_DISTANCE) < SNAP_DISTANCE_PROXIMITY && Math.abs(dist - SNAP_DISTANCE) < minHorizontalDistance) {
        minHorizontalDistance = Math.abs(dist - SNAP_DISTANCE);
        result.snapPosition.y = nodeBBounds.bottom + SNAP_DISTANCE;
        const linePos = nodeBBounds.bottom + SNAP_DISTANCE / 2;
        result.lines = result.lines.filter(l => l.y1 !== l.y2 || l.style === 'align');
        result.lines.push({
          x1: nodeABounds.hCenter - FIXED_LINE_HALF_LENGTH, y1: linePos,
          x2: nodeABounds.hCenter + FIXED_LINE_HALF_LENGTH, y2: linePos,
          style: 'fixed'
        });
    }
  });

  // --- Stage 2: Distribution Snapping ---
  if (enableDistributionSnapping) {
    otherNodes.forEach((nodeB) => {
      let absPosNodeB: XYPosition | undefined;
      if (nodeB.positionAbsolute) {
        absPosNodeB = nodeB.positionAbsolute;
      } else {
        if (nodeB.parentId) {
          const parentOfB = nodes.find(n => n.id === nodeB.parentId);
          if (parentOfB?.positionAbsolute) {
            absPosNodeB = { x: parentOfB.positionAbsolute.x + nodeB.position.x, y: parentOfB.positionAbsolute.y + nodeB.position.y };
          }
        } else {
          absPosNodeB = nodeB.position;
        }
      }
      if (!absPosNodeB) return;

      const nodeBBounds = {
            left: absPosNodeB.x, 
            right: absPosNodeB.x + nodeB.width,
            top: absPosNodeB.y, 
            bottom: absPosNodeB.y + nodeB.height,
            hCenter: absPosNodeB.x + nodeB.width / 2,
            vCenter: absPosNodeB.y + nodeB.height / 2,
            width: nodeB.width, 
            height: nodeB.height,
      };

      otherNodes.forEach((nodeC) => {
        if (nodeC.id === nodeB.id) return;
        
        let absPosNodeC: XYPosition | undefined;
        if (nodeC.positionAbsolute) {
          absPosNodeC = nodeC.positionAbsolute;
        } else {
          if (nodeC.parentId) {
            const parentOfC = nodes.find(n => n.id === nodeC.parentId);
            if (parentOfC?.positionAbsolute) {
              absPosNodeC = { x: parentOfC.positionAbsolute.x + nodeC.position.x, y: parentOfC.positionAbsolute.y + nodeC.position.y };
            }
          } else {
            absPosNodeC = nodeC.position;
          }
        }
        if (!absPosNodeC) return; // continue for inner loop

        const nodeCBounds = {
            left: absPosNodeC.x, 
            right: absPosNodeC.x + nodeC.width,
            top: absPosNodeC.y, 
            bottom: absPosNodeC.y + nodeC.height,
            hCenter: absPosNodeC.x + nodeC.width / 2,
            vCenter: absPosNodeC.y + nodeC.height / 2,
            width: nodeC.width, 
            height: nodeC.height,
        };

        const addDistributionSnap = (
            axis: 'x' | 'y',
            snapPosValue: number,
            gapLine1Start: number, gapLine1End: number,
            gapLine2Start: number, gapLine2End: number,
            currentMinDistance: number
        ): number => {
            const d = Math.abs((axis === 'x' ? nodeABounds.left : nodeABounds.top) - snapPosValue);
            if (d < DISTRIBUTION_SNAP_PROXIMITY && d < currentMinDistance) {
                if (axis === 'x') {
                    result.snapPosition.x = snapPosValue;
                    result.lines = result.lines.filter(l => l.x1 !== l.x2 || l.style === 'align'); // Keep align, remove other fixed/spacing
                    result.lines.push({
                        x1: gapLine1Start, y1: Math.min(nodeABounds.vCenter, nodeBBounds.vCenter, nodeCBounds.vCenter) - 5, // Short vertical lines
                        x2: gapLine1End, y2: Math.max(nodeABounds.vCenter, nodeBBounds.vCenter, nodeCBounds.vCenter) + 5, style: 'spacing'
                    });
                    result.lines.push({
                        x1: gapLine2Start, y1: Math.min(nodeABounds.vCenter, nodeBBounds.vCenter, nodeCBounds.vCenter) - 5,
                        x2: gapLine2End, y2: Math.max(nodeABounds.vCenter, nodeBBounds.vCenter, nodeCBounds.vCenter) + 5, style: 'spacing'
                    });
                } else {
                    result.snapPosition.y = snapPosValue;
                    result.lines = result.lines.filter(l => l.y1 !== l.y2 || l.style === 'align');
                    result.lines.push({
                        x1: Math.min(nodeABounds.hCenter, nodeBBounds.hCenter, nodeCBounds.hCenter) - 5, y1: gapLine1Start, // Short horizontal lines
                        x2: Math.max(nodeABounds.hCenter, nodeBBounds.hCenter, nodeCBounds.hCenter) + 5, y2: gapLine1End, style: 'spacing'
                    });
                    result.lines.push({
                        x1: Math.min(nodeABounds.hCenter, nodeBBounds.hCenter, nodeCBounds.hCenter) - 5, y1: gapLine2Start,
                        x2: Math.max(nodeABounds.hCenter, nodeBBounds.hCenter, nodeCBounds.hCenter) + 5, y2: gapLine2End, style: 'spacing'
                    });
                }
                return d; // Return new min distance
            }
            return currentMinDistance; // No snap, return original min distance
        };
        
        // Horizontal Distribution
        // Case 1: B -- A -- C (A is in the middle)
        // Target: A.left - B.right = C.left - A.right  => A.left = (B.right + C.left - A.width) / 2
        if (nodeBBounds.right < nodeCBounds.left - nodeABounds.width) { // B must be to the left of C with enough space for A
            const targetALeft = (nodeBBounds.right + nodeCBounds.left - nodeABounds.width) / 2;
            const gap = targetALeft - nodeBBounds.right;
            if (gap > 0) { // Ensure positive gap
                 minVerticalDistance = addDistributionSnap('x', targetALeft, 
                    nodeBBounds.right + gap / 2, nodeBBounds.right + gap / 2, // Line 1 at mid of first gap
                    targetALeft + nodeABounds.width + gap / 2, targetALeft + nodeABounds.width + gap / 2, // Line 2 at mid of second gap
                    minVerticalDistance);
            }
        }
        // Case 2: A -- B -- C (B is in the middle)
        // Target: B.left - A.right = C.left - B.right => A.right = B.left - (C.left - B.right)
        if (nodeABounds.left + nodeABounds.width < nodeBBounds.left && nodeBBounds.right < nodeCBounds.left) { // A left of B, B left of C
            const gapBC = nodeCBounds.left - nodeBBounds.right;
            const targetARight = nodeBBounds.left - gapBC;
            const targetALeft = targetARight - nodeABounds.width;
            minVerticalDistance = addDistributionSnap('x', targetALeft,
                targetARight + gapBC / 2, targetARight + gapBC / 2,
                nodeBBounds.right + gapBC / 2, nodeBBounds.right + gapBC / 2,
                minVerticalDistance);
        }
        // Case 3: B -- C -- A (C is in the middle)
        // Target: C.left - B.right = A.left - C.right => A.left = C.right + (C.left - B.right)
        if (nodeBBounds.right < nodeCBounds.left && nodeCBounds.right < nodeABounds.left) { // B left of C, C left of A
            const gapBC = nodeCBounds.left - nodeBBounds.right;
            const targetALeft = nodeCBounds.right + gapBC;
            minVerticalDistance = addDistributionSnap('x', targetALeft,
                nodeBBounds.right + gapBC / 2, nodeBBounds.right + gapBC / 2, // Line 1 at mid of first gap (B-C)
                nodeCBounds.right + gapBC / 2, nodeCBounds.right + gapBC / 2, // Line 2 at mid of second gap (C-A)
                minVerticalDistance);
        }
        
        // Vertical Distribution
        // Case 1: B -- A -- C (A in middle, B above A, A above C)
        // Target: A.top - B.bottom = C.top - A.bottom => A.top = (B.bottom + C.top - A.height) / 2
        if (nodeBBounds.bottom < nodeCBounds.top - nodeABounds.height) {
            const targetATop = (nodeBBounds.bottom + nodeCBounds.top - nodeABounds.height) / 2;
            const gap = targetATop - nodeBBounds.bottom;
            if (gap > 0) {
                minHorizontalDistance = addDistributionSnap('y', targetATop,
                    nodeBBounds.bottom + gap / 2, nodeBBounds.bottom + gap / 2,
                    targetATop + nodeABounds.height + gap / 2, targetATop + nodeABounds.height + gap / 2,
                    minHorizontalDistance);
            }
        }
        // Case 2: A -- B -- C (B in middle, A above B, B above C)
        // Target: B.top - A.bottom = C.top - B.bottom => A.bottom = B.top - (C.top - B.bottom)
        if (nodeABounds.top + nodeABounds.height < nodeBBounds.top && nodeBBounds.bottom < nodeCBounds.top) {
            const gapBC = nodeCBounds.top - nodeBBounds.bottom;
            const targetABottom = nodeBBounds.top - gapBC;
            const targetATop = targetABottom - nodeABounds.height;
            minHorizontalDistance = addDistributionSnap('y', targetATop,
                targetABottom + gapBC / 2, targetABottom + gapBC / 2,
                nodeBBounds.bottom + gapBC / 2, nodeBBounds.bottom + gapBC / 2,
                minHorizontalDistance);
        }
        // Case 3: B -- C -- A (C in middle, B above C, C above A)
        // Target: C.top - B.bottom = A.top - C.bottom => A.top = C.bottom + (C.top - B.bottom)
        if (nodeBBounds.bottom < nodeCBounds.top && nodeCBounds.bottom < nodeABounds.top) {
            const gapBC = nodeCBounds.top - nodeBBounds.bottom;
            const targetATop = nodeCBounds.bottom + gapBC;
            minHorizontalDistance = addDistributionSnap('y', targetATop,
                nodeBBounds.bottom + gapBC / 2, nodeBBounds.bottom + gapBC / 2, // Line 1 at mid of first gap (B-C)
                nodeCBounds.bottom + gapBC / 2, nodeCBounds.bottom + gapBC / 2, // Line 2 at mid of second gap (C-A)
                minHorizontalDistance);
        }
      });
    });
  }

  // Convert snapPosition to be relative to the parent if the node is in a group
  if (parentId && nodeA) { // nodeA is guaranteed to be defined if we reach here
    const parentNode = nodes.find(n => n.id === parentId);
    if (parentNode?.positionAbsolute) {
      if (typeof result.snapPosition.x === 'number') {
        result.snapPosition.x -= parentNode.positionAbsolute.x;
      }
      if (typeof result.snapPosition.y === 'number') {
        result.snapPosition.y -= parentNode.positionAbsolute.y;
      }
    }
    // If parentNode or parentNode.positionAbsolute is not found,
    // snapPosition remains absolute. This might be an issue if the caller
    // strictly expects relative positions for child nodes. However,
    // for an active drag, parent positionAbsolute should typically be available.
  }

  return result;
}
